import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { getEditorMediaFrameClassName } from '@/components/blocks/editor/media-frame';
import { AnnotatedRichText } from '@/components/blocks/editor/proposal-diff/annotations';
import { ButtonBlock } from '@/components/blocks/primitives/button-block';
import {
  IMAGE_BLOCK_SHAPE_VALUES,
  type ImageBlockShape,
} from '@/components/blocks/primitives/image-block';
import {
  type ImageBlockFit,
  resolveImagePresentation,
} from '@/components/blocks/primitives/image-presentation';
import {
  ImageWithLoading,
  NoImagePlaceholder,
} from '@/components/blocks/section-image/image-with-loading';
import { ControlledRichTextEditor } from '@/components/editor/controlled-rich-text-editor';
import { placeholderForSlot } from '@/components/editor/inline-rich-text-placeholders';
import {
  INLINE_RICH_TEXT_EDIT_CURSOR_CLASS,
  inlineRichTextFieldChromeClass,
  inlineRichTextPreviewCursorClass,
} from '@/components/editor/inline-rich-text-surface';
import { FieldEditToolbar } from '@/components/editor/toolbar/field-edit-toolbar';
import { useCollectionSelectThenEditField } from '@/components/editor/use-collection-select-then-edit-field';
import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import { ButtonEditorPopover } from '@/components/shared/button-editor-popover';
import { ImageSettingsPopover } from '@/components/shared/image-settings-modal';
import { Button } from '@/components/ui/button';
import { useBlockSettingsState } from '@/hooks/use-block-settings-state';
import { useClickOutside } from '@/hooks/use-click-outside';
import { useSiteButtons } from '@/hooks/use-site-buttons';
import { isSvgImage } from '@/lib/assets-config';
import { buildButtonPropsForPersistence, readButtonConfig } from '@/lib/blocks/button-links';
import type { CollectionCardField, CollectionItem } from '@/lib/blocks/collection/blocks';
import { isEditorPortalTarget } from '@/lib/editor-portal-targets';
import {
  buildFocusPointStyle,
  DEFAULT_FOCUS_POINT,
  pickStringUnion,
  readImageValue,
  readNumber,
  readRecord,
  readString,
} from '@/lib/prop-utils';
import { normalizeRichTextContent } from '@/lib/rich-text/normalize-rich-text';
import {
  CollectionBlockIdContext,
  useCollectionFieldDiff,
} from '@/lib/section-proposal-diff/section-review-context';
import {
  extractDiffText,
  isPlainTextFieldDiff,
  isRichTextFieldDiff,
} from '@/lib/section-proposal-diff/types';
import { cn } from '@/lib/utils';
import type { ButtonConfig } from '@/types/button';
import type { ImageSuggestionTarget, ImageValue } from '@/types/image';
import { getImageAlt, getImageUrl, isPendingAIImage } from '@/types/image';
import { useMarqueePause } from './sortable/use-marquee-pause';

export function CollectionItemTextField({
  value,
  onUpdate,
  dragDisabled,
  itemId,
  slot,
}: {
  value: unknown;
  onUpdate: (v: unknown, commit?: boolean) => void;
  /** When true, preview uses default cursor (card not draggable). */
  dragDisabled?: boolean;
  itemId: string;
  slot: string;
}) {
  const pageSection = usePageSectionContext();
  const editorSectionUiKey = pageSection?.editorSectionUiKey;
  const fieldDiff = useCollectionFieldDiff(itemId, slot);
  const {
    rootRef: anchorRef,
    isSelected,
    isEditing,
    showEditToolbar,
    tabIndex,
    handleEnterEdit,
    handleExitEditing,
    handleEditorFocusChange,
    shellEventHandlers,
  } = useCollectionSelectThenEditField(editorSectionUiKey);
  const resolvedContent = normalizeRichTextContent(value);
  useMarqueePause(isSelected || isEditing);

  return (
    <div
      ref={anchorRef}
      data-block-text-editor-root
      tabIndex={tabIndex}
      className={
        isEditing
          ? INLINE_RICH_TEXT_EDIT_CURSOR_CLASS
          : inlineRichTextPreviewCursorClass(dragDisabled)
      }
      {...shellEventHandlers}
    >
      <div
        data-block-text-preview-root={isEditing ? undefined : true}
        className={cn('relative', inlineRichTextFieldChromeClass({ isSelected, isEditing }))}
      >
        {!isEditing && fieldDiff?.hasChanges && isRichTextFieldDiff(fieldDiff) ? (
          <div className={cn('tiptap', inlineRichTextPreviewCursorClass(dragDisabled))}>
            <AnnotatedRichText fieldDiff={fieldDiff} />
          </div>
        ) : (
          <ControlledRichTextEditor
            value={resolvedContent}
            isEditing={isEditing}
            onChange={onUpdate}
            onFocusChange={handleEditorFocusChange}
            onExitEditing={handleExitEditing}
            toolbarAnchorRef={anchorRef}
            placeholder={placeholderForSlot(slot)}
            previewClassName={
              !isEditing ? inlineRichTextPreviewCursorClass(dragDisabled) : undefined
            }
          />
        )}
        {showEditToolbar ? (
          <FieldEditToolbar anchorRef={anchorRef} label="Edit text" onEdit={handleEnterEdit} />
        ) : null}
      </div>
    </div>
  );
}

export function CollectionItemImageField({
  field,
  value,
  onUpdate,
  itemId,
}: {
  field: CollectionCardField;
  value: unknown;
  onUpdate: (v: unknown, commit?: boolean) => void;
  itemId: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const collectionBlockId = useContext(CollectionBlockIdContext);
  const imageSuggestionTarget = useMemo<ImageSuggestionTarget | undefined>(
    () =>
      collectionBlockId
        ? { kind: 'collectionItem', blockId: collectionBlockId, itemId }
        : undefined,
    [collectionBlockId, itemId],
  );
  const image = readImageValue(value);
  const isSvg = isSvgImage(image);
  useMarqueePause(isSelected || isOpen);

  const hasImage = image !== null && image !== undefined;
  const hasRenderableImage = !!getImageUrl(image) || isPendingAIImage(image);
  const bp = field.blockProps ?? {};
  const itemRecord = useMemo<Record<string, unknown> | null>(
    () => (value && typeof value === 'object' ? (value as Record<string, unknown>) : null),
    [value],
  );
  const itemProps = itemRecord ?? {};
  const itemFit = readString(itemProps.fit);
  const itemShape = readString(itemProps.shape);
  const resolvedShape = pickStringUnion(
    itemShape ?? readString(bp.shape),
    IMAGE_BLOCK_SHAPE_VALUES,
    'rounded',
  ) as ImageBlockShape;
  const isCircle = resolvedShape === 'circle';
  const isSharp = resolvedShape === 'sharp';
  const presentation = resolveImagePresentation({
    fit: itemFit ?? bp.fit,
    aspect: bp.aspect,
    isSvg,
    isCircle,
  });
  const fitClass = presentation.fitClassName;
  const aspectClass = presentation.aspectClassName;
  const resolvedFocusX = readNumber(itemProps.focusX) ?? DEFAULT_FOCUS_POINT;
  const resolvedFocusY = readNumber(itemProps.focusY) ?? DEFAULT_FOCUS_POINT;
  const isContain = presentation.fit === 'contain';
  const focusStyle = !isContain ? buildFocusPointStyle(resolvedFocusX, resolvedFocusY) : undefined;
  const editorMediaFrameClassName = getEditorMediaFrameClassName({
    isPageEditor: true,
    isEmpty: !hasRenderableImage,
    isSelected,
  });

  const applyItemChange = useCallback(
    (v: Record<string, unknown> | null, commit = true) => onUpdate(v, commit),
    [onUpdate],
  );

  const {
    persistMedia,
    previewMediaDelete,
    applyPreview,
    handleSave: hookSave,
    handleCancel: hookCancel,
  } = useBlockSettingsState({
    open: isOpen,
    currentValue: itemRecord,
    applyChange: applyItemChange,
  });

  useClickOutside(wrapperRef, {
    enabled: isSelected,
    capture: true,
    shouldIgnoreTarget: isEditorPortalTarget,
    onDismiss: () => setIsSelected(false),
  });

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setIsOpen(false);
      setIsSelected(false);
    }
  }, []);

  const handleImageChange = useCallback(
    (img: ImageValue | null) => {
      if (img === null) {
        previewMediaDelete();
      } else {
        persistMedia(img as unknown as Record<string, unknown>);
      }
    },
    [persistMedia, previewMediaDelete],
  );

  const handleAltTextChange = useCallback(
    (alt: string) => {
      const current = readImageValue(value);
      if (current) {
        applyPreview({ ...current, alt });
      }
    },
    [applyPreview, value],
  );

  const handleFitChange = useCallback(
    (fit: ImageBlockFit) => applyPreview({ fit }),
    [applyPreview],
  );

  const handleShapeChange = useCallback(
    (shape: ImageBlockShape) => applyPreview({ shape }),
    [applyPreview],
  );

  const handleFocusChange = useCallback(
    (fx: number, fy: number) => applyPreview({ focusX: fx, focusY: fy }),
    [applyPreview],
  );

  const handleSave = useCallback(() => {
    hookSave();
    setIsSelected(false);
  }, [hookSave]);

  const handleCancel = useCallback(() => {
    hookCancel();
    setIsSelected(false);
  }, [hookCancel]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      tabIndex={isOpen ? -1 : 0}
      onClick={() => {
        if (!isOpen) setIsSelected(true);
      }}
      onDoubleClick={(e) => {
        if (isOpen) return;
        e.stopPropagation();
        setIsOpen(true);
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
      }}
    >
      {isCircle ? (
        <div
          className={cn(
            'aspect-square w-full overflow-hidden rounded-full',
            editorMediaFrameClassName,
          )}
        >
          {hasImage ? (
            <ImageWithLoading
              image={value as ImageValue | null | undefined}
              alt={getImageAlt(image)}
              className="h-full w-full object-cover"
              style={focusStyle}
              fallback={<div className="h-full w-full bg-muted/40" />}
            />
          ) : (
            <NoImagePlaceholder />
          )}
        </div>
      ) : (
        <div
          className={cn(
            'w-full overflow-hidden',
            isContain && 'flex items-center justify-center',
            !isSharp && 'rounded-md',
            aspectClass,
            editorMediaFrameClassName,
          )}
        >
          {hasImage ? (
            <ImageWithLoading
              image={value as ImageValue | null | undefined}
              alt={getImageAlt(image)}
              className={cn(
                isContain ? 'max-h-full max-w-full' : cn('h-full w-full', fitClass),
                !isSharp && 'rounded-md',
              )}
              style={focusStyle}
              fallback={<div className="h-full w-full bg-muted/40" />}
            />
          ) : (
            <NoImagePlaceholder />
          )}
        </div>
      )}
      {isSelected && !isOpen && (
        <FieldEditToolbar
          anchorRef={wrapperRef}
          label="Edit image"
          ariaLabel="Image field actions"
          onEdit={() => setIsOpen(true)}
        />
      )}
      <ImageSettingsPopover
        duplicateOnCrop
        open={isOpen}
        onOpenChange={handleOpenChange}
        image={image}
        fit={presentation.requestedFit}
        shape={resolvedShape}
        showSettings
        selectedAssetId={image?.assetId}
        initialImageUrl={image?.url}
        anchorRef={wrapperRef}
        enableCreateImage
        imageSuggestionTarget={imageSuggestionTarget}
        onImageChange={handleImageChange}
        onAltTextChange={handleAltTextChange}
        onFitChange={handleFitChange}
        onShapeChange={handleShapeChange}
        focusX={resolvedFocusX}
        focusY={resolvedFocusY}
        onFocusChange={handleFocusChange}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
}

export function CollectionItemButtonField({
  value,
  onUpdate,
  itemId,
  slot,
}: {
  value: unknown;
  onUpdate: (v: Record<string, unknown>, commit?: boolean) => void;
  itemId?: string;
  slot?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [liveConfig, setLiveConfig] = useState<ButtonConfig | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const btnConfig = readButtonConfig(readRecord(value) ?? {});
  const fieldDiff = useCollectionFieldDiff(itemId ?? '', slot ?? '');
  const { shape, buttonStyle: siteButtonStyle } = useSiteButtons();
  useMarqueePause(isOpen);

  const handleOpenChange = (open: boolean) => {
    if (!open) setLiveConfig(null);
    setIsOpen(open);
  };

  const handleSave = (newConfig: ButtonConfig) => {
    onUpdate(buildButtonPropsForPersistence(readRecord(value) ?? {}, newConfig));
    setIsOpen(false);
    setLiveConfig(null);
  };

  if (fieldDiff?.hasChanges && isPlainTextFieldDiff(fieldDiff)) {
    const { oldText, newText } = extractDiffText(fieldDiff.segments);
    const colorScheme =
      ({ primary: 'primary', secondary: 'secondary' } as const)[btnConfig.style ?? 'primary'] ??
      'primary';
    return (
      <Button
        variant="solid"
        colorScheme={colorScheme}
        shape={shape}
        buttonStyle={siteButtonStyle}
        className="pointer-events-none min-h-9 h-auto max-w-full min-w-0 shrink whitespace-normal break-words text-center flex-col gap-0.5"
      >
        <mark
          className="flex items-center gap-1.5 rounded-sm px-1 text-inherit line-through opacity-80"
          style={{ backgroundColor: 'var(--diff-removed)' }}
        >
          {oldText}
        </mark>
        <mark
          className="flex items-center gap-1.5 rounded-sm px-1 text-inherit"
          style={{ backgroundColor: 'var(--diff-added)' }}
        >
          {newText}
        </mark>
      </Button>
    );
  }

  return (
    <div
      ref={anchorRef}
      className="relative"
      onDoubleClick={(e) => {
        if (isOpen) return;
        e.stopPropagation();
        setIsOpen(true);
      }}
    >
      <ButtonBlock {...(liveConfig ?? btnConfig)} />
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="absolute inset-0 cursor-pointer"
        aria-label="Edit button"
      />
      <ButtonEditorPopover
        open={isOpen}
        initialValue={btnConfig}
        onChange={setLiveConfig}
        onSave={handleSave}
        onOpenChange={handleOpenChange}
        anchorRef={anchorRef}
      />
    </div>
  );
}

export function CollectionItemField({
  field,
  value,
  onUpdate,
  dragDisabled,
  itemId,
}: {
  field: CollectionCardField;
  value: unknown;
  onUpdate: (v: unknown, commit?: boolean) => void;
  dragDisabled?: boolean;
  itemId: string;
}) {
  switch (field.kind) {
    case 'image':
      return (
        <CollectionItemImageField field={field} value={value} onUpdate={onUpdate} itemId={itemId} />
      );
    case 'button':
      return (
        <CollectionItemButtonField
          value={value}
          onUpdate={onUpdate}
          itemId={itemId}
          slot={field.slot}
        />
      );
    default:
      return (
        <CollectionItemTextField
          value={value}
          onUpdate={onUpdate}
          dragDisabled={dragDisabled}
          itemId={itemId}
          slot={field.slot}
        />
      );
  }
}

export function makeEditorRenderField(
  item: CollectionItem,
  onUpdate: (updated: CollectionItem, commit?: boolean) => void,
  cardDragDisabled?: boolean,
) {
  return (field: CollectionCardField, value: unknown) => (
    <CollectionItemField
      field={field}
      value={value}
      onUpdate={(v, commit) => onUpdate({ ...item, [field.slot]: v }, commit)}
      dragDisabled={cardDragDisabled}
      itemId={item.id}
    />
  );
}
